/**
 * Ein kleiner Satz handgezeichneter SVG-Icons.
 *
 * Ersetzt die frueheren Emoji. Emoji als Icon-Ersatz sehen auf jedem
 * Betriebssystem anders aus, lassen sich nicht einfaerben, skalieren
 * nicht mit der Schrift und wirken beliebig -- auf einem Fernseher aus
 * fuenf Metern besonders.
 *
 * Bewusst rein geometrisch: Striche, Kreise, Polygone. Keine
 * gezeichneten Figuren oder Szenen -- die geraten immer billig.
 */

type IconProps = {
  /** Groesse relativ zur Schrift; Standard passt sich dem Kontext an. */
  size?: string | number;
  className?: string;
};

function svgProps(size: IconProps['size'], className?: string) {
  return {
    viewBox: '0 0 24 24',
    className: `icon ${className ?? ''}`.trim(),
    style: size === undefined ? undefined : { width: size, height: size },
    'aria-hidden': true,
    focusable: false,
  } as const;
}

/** Lautsprecher, stumm. */
export function IconVolumeOff({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <line x1="17" y1="9" x2="22" y2="15" />
      <line x1="22" y1="9" x2="17" y2="15" />
    </svg>
  );
}

/** Lautsprecher, leise (eine Welle). */
export function IconVolumeLow({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M17 10a4 4 0 0 1 0 4" />
    </svg>
  );
}

/** Lautsprecher, laut (zwei Wellen). */
export function IconVolumeHigh({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M4 9v6h4l5 4V5L8 9H4z" />
      <path d="M17 10a4 4 0 0 1 0 4" />
      <path d="M20 7.5a8 8 0 0 1 0 9" />
    </svg>
  );
}

/** Kreisender Pfeil -- Song von vorn. */
export function IconReplay({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <polyline points="20 4 20 9 15 9" />
    </svg>
  );
}

/** Drei Punkte -- weitere Aktionen. */
export function IconMore({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)} className={`icon icon--filled ${className ?? ''}`.trim()}>
      <circle cx="5" cy="12" r="1.7" />
      <circle cx="12" cy="12" r="1.7" />
      <circle cx="19" cy="12" r="1.7" />
    </svg>
  );
}

/** Schliessen. */
export function IconClose({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

/** Note -- markiert den aktiven Lautsprecher. */
export function IconNote({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M9 18V5l10-2v13" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </svg>
  );
}

/** Krone -- Gewinner. */
export function IconCrown({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <path d="M3 8l4 4 5-7 5 7 4-4-2 11H5L3 8z" />
    </svg>
  );
}

/** Pfeilspitze -- auf- und zuklappen. */
export function IconChevron({ up, size, className }: IconProps & { up?: boolean }) {
  return (
    <svg {...svgProps(size, className)}>
      <polyline points={up ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
    </svg>
  );
}

/** Plus -- freie Einfuegestelle in der Zeitleiste. */
export function IconPlus({ size, className }: IconProps) {
  return (
    <svg {...svgProps(size, className)}>
      <line x1="12" y1="6" x2="12" y2="18" />
      <line x1="6" y1="12" x2="18" y2="12" />
    </svg>
  );
}
